import { groupTree, containerChildren, boundsOf } from "./core/structure";

// Menu-aware editing for flowing sections (schema v2 blocks). A dish inside a
// stack/row/grid is positioned by its section, so dragging it reorders it or
// moves it to another section instead of storing a stray position.
const flowing = (doc, id) => { const b = doc.blocks?.[id]; return b && b.layout && b.layout.mode !== "free"; };
const pinned = (doc, parent, child) => (child.groupId
  ? doc.blocks?.[child.groupId]?.placement?.mode === "fixed"
  : (parent.layout?.pinned || []).includes(child.key));

// The flowing container child that exactly matches a selection, if any.
// With { pinnedOnly: true } it finds a pinned child instead.
export function flowChildFor(doc, page, ids, { pinnedOnly = false } = {}) {
  if (!doc.blocks || !ids.length) return null;
  const tree = groupTree(page);
  const set = new Set(ids);
  for (const [id, g] of tree.groups) {
    if (!flowing(doc, id)) continue;
    for (const child of containerChildren(tree, id, doc.blocks[id])) {
      const members = child.nodes.map((n) => n.id);
      const attached = page.elements.filter((n) => n.attachedTo && members.includes(n.attachedTo)).map((n) => n.id);
      const all = new Set([...members, ...attached]);
      if (members.every((m) => set.has(m)) && [...set].every((m) => all.has(m)) && pinned(doc, doc.blocks[id], child) === pinnedOnly)
        return { containerId: id, child, parent: g.parent };
    }
  }
  return null;
}

// New child order after dropping `child` with its box's top-left moved by (dx, dy).
export function reorderForDrop(doc, page, containerId, childKey, dx, dy) {
  const tree = groupTree(page);
  const block = doc.blocks[containerId];
  const children = containerChildren(tree, containerId, block).filter((c) => !pinned(doc, block, c));
  const moving = children.find((c) => c.key === childKey);
  if (!moving) return null;
  const horizontal = block.layout.mode === "row";
  const grid = block.layout.mode === "grid";
  const cx = moving.box.x + moving.box.w / 2 + dx, cy = moving.box.y + moving.box.h / 2 + dy;
  const others = children.filter((c) => c !== moving);
  let index = others.length;
  for (let i = 0; i < others.length; i++) {
    const b = others[i].box;
    // Row: left of centre. Grid: an earlier row, or this row and left of centre. Stack: above centre.
    const before = horizontal ? cx < b.x + b.w / 2
      : grid ? cy < b.y || (cy <= b.y + b.h && cx < b.x + b.w / 2)
      : cy < b.y + b.h / 2;
    if (before) { index = i; break; }
  }
  const order = others.map((c) => c.key);
  order.splice(index, 0, childKey);
  const all = containerChildren(tree, containerId, block).map((c) => c.key);
  const rest = all.filter((k) => !order.includes(k));
  const next = [...order, ...rest];
  return next.join("|") === all.join("|") ? null : next;
}

// A flowing section on the page whose bounds contain the point, other than `except`.
export function containerAt(doc, page, point, except) {
  const tree = groupTree(page);
  let found = null;
  for (const [id] of tree.groups) {
    if (id === except || !flowing(doc, id)) continue;
    const box = boundsOf(tree.groups.get(id).nodes.concat(...tree.groups.get(id).children.map((c) => collect(tree, c))));
    if (box && point.x >= box.x && point.x <= box.x + box.w && point.y >= box.y - 12 && point.y <= box.y + box.h + 12) found = { id, box };
  }
  return found;
}
function collect(tree, id) {
  const g = tree.groups.get(id);
  return [...g.nodes, ...g.children.flatMap((c) => collect(tree, c))];
}

// Move a child group into another container: its nodes' group paths change
// parent, and it joins the target order at the drop position.
export function moveChildTo(doc, pageId, child, fromId, toId, point) {
  const page = doc.pages.find((p) => p.id === pageId);
  const tree = groupTree(page);
  const targetPath = [];
  for (let id = toId; id; id = tree.groups.get(id).parent) targetPath.unshift({ id, name: tree.groups.get(id).name });
  const ids = new Set(child.nodes.map((n) => n.id));
  const pages = doc.pages.map((p) => p.id !== pageId ? p : { ...p, elements: p.elements.map((n) => {
    if (!ids.has(n.id)) return n;
    const path = n.groupPath || [];
    const at = path.findIndex((g) => g.id === child.groupId);
    const groupPath = [...targetPath, ...path.slice(at)];
    return { ...n, groupPath, groupId: groupPath[groupPath.length - 1].id };
  }) });
  const next = { ...doc, pages };
  const toBlock = doc.blocks[toId];
  const siblings = containerChildren(groupTree(pages.find((p) => p.id === pageId)), toId, toBlock).filter((c) => c.key !== child.key);
  let index = siblings.findIndex((c) => point.y < c.box.y + c.box.h / 2);
  if (index < 0) index = siblings.length;
  const order = siblings.map((c) => c.key);
  order.splice(index, 0, child.key);
  const fromBlock = doc.blocks[fromId];
  return {
    ...next,
    blocks: {
      ...doc.blocks,
      [toId]: { ...toBlock, layout: { ...toBlock.layout, order } },
      [fromId]: { ...fromBlock, layout: { ...fromBlock.layout, order: (fromBlock.layout.order || []).filter((k) => k !== child.key) } },
    },
  };
}

// Spacing handles: the gap before each flowing child after the first.
export function gapHandles(doc, displayPage, containerId) {
  const block = doc.blocks?.[containerId];
  if (!block || !["stack", "grid"].includes(block.layout?.mode)) return [];
  const tree = groupTree(displayPage);
  if (!tree.groups.has(containerId)) return [];
  const children = containerChildren(tree, containerId, block).filter((c) => !pinned(doc, block, c));
  const cols = block.layout.mode === "grid" ? Math.max(1, block.layout.columns || 2) : 1;
  const out = [];
  for (let i = cols; i < children.length; i += cols) {
    const prev = children.slice(i - cols, i), c = children[i];
    const top = Math.max(...prev.map((p) => p.box.y + p.box.h));
    const box = boundsOf(children.flatMap((x) => x.nodes));
    const gap = block.layout.spaceBefore?.[c.key] ?? block.layout.gap ?? 0;
    out.push({ key: c.key, x: box.x, w: box.w, y: top, gap });
  }
  return out;
}
// scope "gap": only this gap; "all": the section's shared gap (clears exceptions).
export function setGap(block, key, gap, scope) {
  const g = Math.round(gap * 100) / 100;
  if (scope === "all") {
    const layout = { ...block.layout, gap: g };
    delete layout.spaceBefore;
    return { ...block, layout };
  }
  return { ...block, layout: { ...block.layout, spaceBefore: { ...(block.layout.spaceBefore || {}), [key]: g } } };
}

// Pin: keep the child exactly where it is shown now; flowing siblings close up.
export function pinChild(authored, display, pageId, containerId, child, pin) {
  const shown = new Map(display.pages.find((p) => p.id === pageId).elements.map((n) => [n.id, n]));
  const ids = new Set(child.nodes.map((n) => n.id));
  const pages = authored.pages.map((p) => p.id !== pageId ? p : { ...p, elements: p.elements.map((n) => {
    const at = shown.get(n.id);
    return pin && (ids.has(n.id) || ids.has(n.attachedTo)) && at ? { ...n, x: at.x, y: at.y } : n;
  }) });
  const blocks = { ...authored.blocks };
  if (child.groupId) blocks[child.groupId] = { ...blocks[child.groupId], placement: { ...(blocks[child.groupId].placement || {}), mode: pin ? "fixed" : "flow" } };
  else {
    const layout = blocks[containerId].layout;
    const list = new Set(layout.pinned || []);
    if (pin) list.add(child.key); else list.delete(child.key);
    blocks[containerId] = { ...blocks[containerId], layout: { ...layout, pinned: [...list] } };
  }
  return { ...authored, pages, blocks };
}
